import React, {useCallback, useEffect, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import Button from "@source/components/UI/buttons/button/button";
import Checkbox from "@source/components/UI/buttons/checkbox/checkbox";
import ConsentController from "@source/api/controllers/consentController";
import ReqError from "@source/api/reqError";
import {ConsentDocument} from "@source/api/models/consentModels";
import {
  canUseResidentPresentationResponse, createResidentConsentRequest, createResidentIdentityRequest,
  getResidentConsentResultCopy,
} from "@source/api/models/consentHelpers";
import appStore from "@source/store/appStore";
import {ConsentDocumentPreview} from "@source/components/consentDocuments/consentDocumentPreview";
import {ConsentRequestState} from "@source/components/consentDocuments";
import styles from "./residentPresentation.module.scss";

const requestError = (error: unknown) => {
  const status = error instanceof ReqError ? error.status : undefined;
  if (status === 400) return "This action could not be recorded. Check the document stage and try again.";
  if (status === 403) return "You do not have permission to present this consent document.";
  if (status === 404) return "This consent document is no longer available.";
  if (status === 409) return "This document changed stage. Return to the resident and refresh the consent history.";
  return error instanceof Error ? error.message : "Unable to complete this request.";
};

const isTerminalPresentationError = (error: unknown) =>
  error instanceof ReqError
  && (error.status === 403 || error.status === 404 || error.status === 409);

export const ResidentPresentation = ({nursingHomeId}: {nursingHomeId: string}) => {
  const history = useHistory();
  const {residentId, documentId} = useParams<{residentId: string; documentId: string}>();
  const [document, setDocument] = useState<ConsentDocument>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [verified, setVerified] = useState(false);
  const [identityChecked, setIdentityChecked] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [submitting, setSubmitting] = useState<"identity" | "consent" | "decline" | null>(null);
  const [result, setResult] = useState<"consent" | "decline" | null>(null);
  const mounted = useRef(true);
  const loadRequest = useRef(0);
  const submission = useRef(false);
  const back = useCallback(
    () => history.push(`/home/residents/${residentId}/consent-documents`),
    [history, residentId],
  );

  const load = useCallback(async () => {
    const requestId = ++loadRequest.current;
    setLoading(true); setError("");
    setDocument(undefined); setVerified(false); setIdentityChecked(false); setPreviewReady(false); setResult(null);
    try {
      const response = await ConsentController.getResidentPresentation(nursingHomeId, documentId);
      if (!mounted.current || requestId !== loadRequest.current) return;
      if (response.resident._id !== residentId) {
        setError("This consent document belongs to a different resident.");
        return;
      }
      if (!canUseResidentPresentationResponse(response)) {
        setError("The resident is not the active signer for this document. Return to the resident to review its current signing stage.");
        return;
      }
      setDocument(response);
      setVerified(Boolean(response.residentIdentityVerification));
    } catch (requestErrorValue) {
      if (mounted.current && requestId === loadRequest.current) setError(requestError(requestErrorValue));
    } finally {
      if (mounted.current && requestId === loadRequest.current) setLoading(false);
    }
  }, [documentId, nursingHomeId, residentId]);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
      loadRequest.current += 1;
      window.document.body.classList.remove(styles.presentationBody);
    };
  }, [load]);
  useEffect(() => {
    if ((verified || result) && document) window.document.body.classList.add(styles.presentationBody);
    return () => window.document.body.classList.remove(styles.presentationBody);
  }, [document, result, verified]);
  useEffect(() => {
    if (!document || verified || result) return;
    const name = `${document.resident.firstName} ${document.resident.lastName}`.trim() || "Resident";
    const header = {
      resident: document.resident,
      title: `Present consent to ${name}`,
      subtitle: "Additional step · Staff identity verification",
      usePreviousStepNavigation: true,
      onBack: back,
      onCancel: back,
    };
    appStore.setConsentResidentHeader(header);
    return () => appStore.resetConsentResidentHeader(header);
  }, [back, document, result, verified]);

  if (loading) return <ConsentRequestState variant="loading" title="Loading resident presentation"/>;
  if (error && !document) return <ConsentRequestState variant="error" title="Resident presentation unavailable" description={error} onRetry={() => void load()}/>;
  if (!document) return null;
  const name = `${document.resident.firstName} ${document.resident.lastName}`.trim();
  const statement = createResidentIdentityRequest(name).identityVerificationStatement;
  const category = document.categorySnapshot?.name || "Legacy consent document";

  const verify = async () => {
    if (submission.current) return;
    submission.current = true;
    setSubmitting("identity"); setError("");
    try {
      const response = await ConsentController.verifyResidentIdentity(nursingHomeId, documentId, createResidentIdentityRequest(name));
      if (!mounted.current) return;
      // This mutation response can omit computed signatories; the successful
      // identity endpoint is authoritative for moving to resident review.
      setDocument(response); setVerified(true);
    } catch (requestErrorValue) {
      if (!mounted.current) return;
      setError(requestError(requestErrorValue));
      if (isTerminalPresentationError(requestErrorValue)) {
        setDocument(undefined);
        setPreviewReady(false);
      }
    }
    finally {
      submission.current = false;
      if (mounted.current) setSubmitting(null);
    }
  };
  const decide = async (decline = false) => {
    if (submission.current) return;
    submission.current = true;
    setSubmitting(decline ? "decline" : "consent"); setError("");
    try {
      const response = decline
        ? await ConsentController.recordResidentDecline(nursingHomeId, documentId)
        : await ConsentController.recordResidentConsent(nursingHomeId, documentId, createResidentConsentRequest());
      if (!mounted.current) return;
      setDocument(response); setResult(decline ? "decline" : "consent");
    } catch (requestErrorValue) {
      if (!mounted.current) return;
      setError(requestError(requestErrorValue));
      if (isTerminalPresentationError(requestErrorValue)) {
        setDocument(undefined);
        setPreviewReady(false);
      }
    }
    finally {
      submission.current = false;
      if (mounted.current) setSubmitting(null);
    }
  };

  if (!verified) return <div className={styles.start}>
    <section className={styles.identity}><span className={styles.identityStyle}>Staff identity check</span><p>This acknowledgement is recorded before the resident-facing consent screen is presented.</p>
      <Checkbox name="resident-identity" value="verified" checked={identityChecked} onChange={(event) => setIdentityChecked(event.target.checked)} label={statement}/>
      {error && <p className={styles.error} role="alert">{error}</p>}
      <div className={styles.actions}><Button variant="outlined" onClick={back}>Present later</Button><Button onClick={() => void verify()} disabled={!identityChecked || Boolean(submitting)} isLoading={submitting === "identity"}>{`Present consent to ${document.resident.firstName}`}</Button></div>
    </section>
  </div>;

  return <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Resident consent presentation">
    <header className={styles.contextBar}><span>{`${document.nursingHome?.name || "Consent document"} · ${document.title}`}</span><strong>Resident on site</strong><span>{name}</span></header>
    <main className={styles.presentationMain}>
      {result ? <section className={styles.result}>
        <h2>{result === "consent" ? "Consent recorded" : "Consent declined"}</h2>
        <p>{result === "consent" ? getResidentConsentResultCopy(document.status) : "The resident declined consent. This document is now closed."}</p>
        <div className={styles.resultDocument}><strong>{document.title}</strong><span>{category}</span><small>{document.residentIdentityVerification?.identityVerificationStatement || "Resident identity was verified on site."}</small></div>
        <Button className={styles.wideButton} onClick={back}>Return to resident</Button>
      </section> : <section className={styles.presentationPanel}>
        <div className={styles.previewArea}><ConsentDocumentPreview className={styles.presentationPreview} documentId={documentId} onReadinessChange={setPreviewReady}/></div>
        <div className={styles.decision}>
          {error && <div className={styles.reviewError} role="alert"><p className={styles.error}>{error}</p><Button size="small" variant="outlined" onClick={back}>Return to resident</Button></div>}
          <p className={styles.acceptance}>By selecting agree, you confirm that you understand this consent document.</p>
          <div className={styles.decisionActions}>
            <Button className={styles.wideButton} onClick={() => void decide()} disabled={!previewReady || Boolean(submitting)} isLoading={submitting === "consent"}>{createResidentConsentRequest().acceptanceStatement}</Button>
            <Button className={styles.wideButton} variant="outlined" onClick={() => void decide(true)} disabled={!previewReady || Boolean(submitting)} isLoading={submitting === "decline"}>I don’t agree</Button>
          </div>
        </div>
      </section>}
    </main>
  </div>;
};
